/**
 * Ported from three/examples/jsm/lines/LineSegmentsGeometry.js (MIT License).
 * Source: https://github.com/mrdoob/three.js/blob/master/examples/jsm/lines/
 *
 * Adapted to TypeScript:
 *   - Strict typing on constructor params and internal state
 *   - GLSL shader extracted to a separate `.glsl.ts` template literal module
 *   - `onUploadCallback` defensive null-ing (Opt 1a) added on instance buffers
 */

import * as THREE from "three";

const _box = new THREE.Box3();
const _vector = new THREE.Vector3();

/**
 * Defensive helper: after the GPU has uploaded an InstancedInterleavedBuffer,
 * null-out the CPU-side typed-array so it can be garbage-collected. The buffer
 * keeps its WebGL counterpart and bookkeeping fields intact. Wrapped in
 * try/catch because some three.js builds make `.array` non-writable, and we
 * don't want a freed-buffer edge case to crash the upload path.
 */
function nullifyArrayAfterUpload(
  buffer: THREE.InstancedInterleavedBuffer,
): void {
  buffer.onUploadCallback = function onUploadDefensiveNullify(
    this: THREE.InstancedInterleavedBuffer,
  ) {
    try {
      // Cast through unknown because three.js types `array` as readonly typed array,
      // but at runtime the property is writable post-upload.
      (this as unknown as { array: ArrayLike<number> | null }).array = null;
    } catch {
      // ignore — non-writable in some builds; GC is best-effort here.
    }
  };
}

/**
 * A series of vertex pairs, forming line segments.
 *
 * This is used in {@link LineSegments2} to describe the shape.
 */
export class LineSegmentsGeometry extends THREE.InstancedBufferGeometry {
  /**
   * This flag can be used for type testing.
   */
  public readonly isLineSegmentsGeometry: true = true;

  public override type: string = "LineSegmentsGeometry";

  /**
   * Constructs a new line segments geometry.
   */
  constructor() {
    super();

    const positions = [
      -1, 2, 0, 1, 2, 0, -1, 1, 0, 1, 1, 0, -1, 0, 0, 1, 0, 0, -1, -1, 0, 1,
      -1, 0,
    ];
    const uvs = [-1, 2, 1, 2, -1, 1, 1, 1, -1, -1, 1, -1, -1, -2, 1, -2];
    const index = [0, 2, 1, 2, 3, 1, 2, 4, 3, 4, 5, 3, 4, 6, 5, 6, 7, 5];

    this.setIndex(index);
    this.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3),
    );
    this.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  }

  /**
   * Applies the given 4x4 transformation matrix to the geometry.
   */
  override applyMatrix4(matrix: THREE.Matrix4): this {
    const start = this.attributes["instanceStart"] as
      | THREE.InterleavedBufferAttribute
      | undefined;
    const end = this.attributes["instanceEnd"] as
      | THREE.InterleavedBufferAttribute
      | undefined;

    if (start !== undefined) {
      start.applyMatrix4(matrix);

      if (end !== undefined) {
        end.applyMatrix4(matrix);
      }

      start.needsUpdate = true;
    }

    if (this.boundingBox !== null) {
      this.computeBoundingBox();
    }

    if (this.boundingSphere !== null) {
      this.computeBoundingSphere();
    }

    return this;
  }

  /**
   * Sets the given line positions for this geometry. The length must be a multiple of six since
   * each line segment is defined by a start end vertex in the pattern `(xyz xyz)`.
   */
  setPositions(array: Float32Array | number[]): this {
    let lineSegments: Float32Array;

    if (array instanceof Float32Array) {
      lineSegments = array;
    } else if (Array.isArray(array)) {
      lineSegments = new Float32Array(array);
    } else {
      // Should be unreachable under TS, but mirrors the original JS undefined-behavior.
      lineSegments = new Float32Array(0);
    }

    const instanceBuffer = new THREE.InstancedInterleavedBuffer(
      lineSegments,
      6,
      1,
    ); // xyz, xyz

    // Opt 1a: free the CPU-side typed array after GPU upload to reduce RAM footprint
    // on large drawings (hundreds of thousands of segments).
    nullifyArrayAfterUpload(instanceBuffer);

    this.setAttribute(
      "instanceStart",
      new THREE.InterleavedBufferAttribute(instanceBuffer, 3, 0),
    ); // xyz
    this.setAttribute(
      "instanceEnd",
      new THREE.InterleavedBufferAttribute(instanceBuffer, 3, 3),
    ); // xyz

    this.instanceCount = (
      this.attributes["instanceStart"] as THREE.InterleavedBufferAttribute
    ).count;

    // three.js clamps every instanced draw to
    // `Math.min(geometry.instanceCount, geometry._maxInstanceCount)`
    // (WebGLRenderer `renderBufferDirect`). `_maxInstanceCount` is a
    // three internal latched EXACTLY ONCE per geometry object — set only
    // while it `=== undefined` (`WebGLBindingStates.setupVertexAttributes`:
    // `if (object.isInstancedMesh !== true && geometry._maxInstanceCount
    // === undefined) geometry._maxInstanceCount = data.meshPerAttribute *
    // data.count`) and cleared ONLY by the geometry's `'dispose'` event
    // (`WebGLGeometries.onGeometryDispose` → `delete geometry._maxInstanceCount`).
    // When a `LineSegmentsGeometry` is REUSED with a DIFFERENT segment
    // count WITHOUT a `dispose()`, the cap stays frozen at the first
    // render's count, so `instanceCount` (set above) is correct but the
    // renderer truncates the draw to the stale cap. This app hits that on
    // two paths: (1) `meshPool` recycles geometry shells across bulk-loads
    // → a bucket rebuilt with more lines rendered only the first-render
    // count = the "2nd / 3rd primitive drawn but invisible" bug; (2) the
    // LOD viewport-cull pass (`FrameLoops`) re-uploads live bucket meshes
    // via `setPositions` when the visible set grows. `delete` clears the
    // latch so three recomputes the cap from the new `instanceStart.count`
    // (count-based — NOT array.length, which the `nullifyArrayAfterUpload`
    // Opt-1a nulls → would recompute 0). Mirrors three.js issues
    // #21488 / #26363 / #19706 / #27205. Cast: `_maxInstanceCount` is
    // absent from the public typings.
    delete (this as unknown as { _maxInstanceCount?: number })
      ._maxInstanceCount;

    this.computeBoundingBox();
    this.computeBoundingSphere();

    return this;
  }

  /**
   * Sets the given line colors for this geometry. The length must be a multiple of six since
   * each line segment is defined by a start end color in the pattern `(rgb rgb)`.
   */
  setColors(array: Float32Array | number[]): this {
    let colors: Float32Array;

    if (array instanceof Float32Array) {
      colors = array;
    } else if (Array.isArray(array)) {
      colors = new Float32Array(array);
    } else {
      colors = new Float32Array(0);
    }

    const instanceColorBuffer = new THREE.InstancedInterleavedBuffer(
      colors,
      6,
      1,
    ); // rgb, rgb

    // Opt 1a: free the CPU-side typed array after GPU upload.
    nullifyArrayAfterUpload(instanceColorBuffer);

    this.setAttribute(
      "instanceColorStart",
      new THREE.InterleavedBufferAttribute(instanceColorBuffer, 3, 0),
    ); // rgb
    this.setAttribute(
      "instanceColorEnd",
      new THREE.InterleavedBufferAttribute(instanceColorBuffer, 3, 3),
    ); // rgb

    return this;
  }

  /**
   * PR-LT3 variant A (A2) — FIX-2 (Codex P2). Bind the per-SEGMENT CELTSCALE
   * reciprocal (1 / CELTSCALE) as a TRUE INSTANCED attribute, exactly aligned
   * with `instanceStart` / `instanceEnd`.
   *
   * # Why this must be instanced (and not a plain BufferAttribute)
   *
   * `LineSegmentsGeometry` draws each SEGMENT as one instance of the shared
   * 8-vertex quad (`instanceStart`/`instanceEnd` are `InstancedInterleavedBuffer`
   * views, mesh-per-attribute 1). A plain `Float32BufferAttribute` has instance
   * divisor 0, so it is indexed by the quad's vertices — every segment instance
   * would reuse the FIRST few entries and CELTSCALE would be wrong for any bucket
   * with mixed per-entity scales. Binding through an `InstancedBufferAttribute`
   * (divisor 1, one value per segment) makes the shader read THIS segment's own
   * scale, matching how `setPositions` builds `instanceStart`/`instanceEnd`.
   *
   * `array` MUST hold exactly ONE value per segment (= `instanceStart.count`).
   */
  setLinetypeScales(array: Float32Array | number[]): this {
    let scales: Float32Array;

    if (array instanceof Float32Array) {
      scales = array;
    } else if (Array.isArray(array)) {
      scales = new Float32Array(array);
    } else {
      scales = new Float32Array(0);
    }

    const instanceScaleBuffer = new THREE.InstancedBufferAttribute(scales, 1, false);

    // Opt 1a parity: free the CPU-side typed array after GPU upload. Wrapped in
    // try/catch — `.array` is non-writable in some three builds.
    instanceScaleBuffer.onUploadCallback = function onUploadDefensiveNullify(
      this: THREE.InstancedBufferAttribute,
    ) {
      try {
        (this as unknown as { array: ArrayLike<number> | null }).array = null;
      } catch {
        // ignore — best-effort GC only.
      }
    };

    this.setAttribute("instanceLtScale", instanceScaleBuffer);

    return this;
  }

  /**
   * Setups this line segments geometry from the given wireframe geometry.
   */
  fromWireframeGeometry(geometry: THREE.WireframeGeometry): this {
    const positionAttr = geometry.attributes["position"];
    if (positionAttr !== undefined) {
      this.setPositions(positionAttr.array as Float32Array);
    }

    return this;
  }

  /**
   * Setups this line segments geometry from the given edges geometry.
   */
  fromEdgesGeometry(geometry: THREE.EdgesGeometry): this {
    const positionAttr = geometry.attributes["position"];
    if (positionAttr !== undefined) {
      this.setPositions(positionAttr.array as Float32Array);
    }

    return this;
  }

  /**
   * Setups this line segments geometry from the given mesh.
   */
  fromMesh(mesh: THREE.Mesh): this {
    this.fromWireframeGeometry(new THREE.WireframeGeometry(mesh.geometry));

    // set colors, maybe

    return this;
  }

  /**
   * Setups this line segments geometry from the given line segments.
   * Assumes the source geometry is not using indices.
   */
  fromLineSegments(lineSegments: THREE.LineSegments): this {
    const geometry = lineSegments.geometry;

    const positionAttr = geometry.attributes["position"];
    if (positionAttr !== undefined) {
      this.setPositions(positionAttr.array as Float32Array); // assumes non-indexed
    }

    // set colors, maybe

    return this;
  }

  override computeBoundingBox(): void {
    if (this.boundingBox === null) {
      this.boundingBox = new THREE.Box3();
    }

    const start = this.attributes["instanceStart"] as
      | THREE.InterleavedBufferAttribute
      | undefined;
    const end = this.attributes["instanceEnd"] as
      | THREE.InterleavedBufferAttribute
      | undefined;

    if (start !== undefined && end !== undefined) {
      // Three.js runtime accepts InterleavedBufferAttribute here even though the
      // current TypeScript definitions narrow the parameter to BufferAttribute.
      // See Box3.setFromBufferAttribute in node_modules/three/src/math/Box3.js.
      this.boundingBox.setFromBufferAttribute(
        start as unknown as THREE.BufferAttribute,
      );

      _box.setFromBufferAttribute(end as unknown as THREE.BufferAttribute);

      this.boundingBox.union(_box);
    }
  }

  override computeBoundingSphere(): void {
    if (this.boundingSphere === null) {
      this.boundingSphere = new THREE.Sphere();
    }

    if (this.boundingBox === null) {
      this.computeBoundingBox();
    }

    const start = this.attributes["instanceStart"] as
      | THREE.InterleavedBufferAttribute
      | undefined;
    const end = this.attributes["instanceEnd"] as
      | THREE.InterleavedBufferAttribute
      | undefined;

    if (start !== undefined && end !== undefined && this.boundingBox !== null) {
      const center = this.boundingSphere.center;

      this.boundingBox.getCenter(center);

      let maxRadiusSq = 0;

      for (let i = 0, il = start.count; i < il; i++) {
        _vector.fromBufferAttribute(start, i);
        maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(_vector));

        _vector.fromBufferAttribute(end, i);
        maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(_vector));
      }

      this.boundingSphere.radius = Math.sqrt(maxRadiusSq);

      if (isNaN(this.boundingSphere.radius)) {
        console.error(
          "THREE.LineSegmentsGeometry.computeBoundingSphere(): Computed radius is NaN. The instanced position data is likely to have NaN values.",
          this,
        );
      }
    }
  }

  override toJSON(): ReturnType<THREE.InstancedBufferGeometry["toJSON"]> {
    // todo
    return super.toJSON();
  }
}
